import { HubFeature } from '@ats/api/cm';
import { LocDefs, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Fulfillment Vouchers|Upload';
const instructionHTML = `
<p>Enter the voucher code provided by CompTia below.</p>
<p>Each code is linked to a specific reward that will be shipped to you once processed.</p>
`;

export type CPFulfillmentVoucherUploadFormLocs =
	| 'CPFulfillmentVoucherUploadFormPageTitle'
	| 'CPFulfillmentVoucherUploadFormSectionTitle'
	| 'CPFulfillmentVoucherUploadFormPageDescription'
	| 'CPFulfillmentVoucherUploadFormInstructions'
	| 'CPFulfillmentVoucherUploadVoucherCodeLabel'
	| 'CPFulfillmentVoucherUploadVoucherCodeRequired'
	| 'CPFulfillmentVoucherUploadFcatIdLabel'
	| 'CPFulfillmentVoucherUploadFcatIdRequired'
	| 'CPFulfillmentVoucherUploadSubmitButton'
	| 'CPFulfillmentVoucherUploadSuccess'
	| 'CPFulfillmentVoucherUploadError'
	| 'CPFulfillmentVoucherUploadErrorNotFound'
	| 'CPFulfillmentVoucherUploadFormNoFulfillments'
	| '11dd20b85ec544efb80d50ae91040ae6';

export const cpFulfillmentVoucherUploadFormLocs: LocDefs<CPFulfillmentVoucherUploadFormLocs> = {
	CPFulfillmentVoucherUploadFormPageTitle: {
		defaultValue: 'Fulfillment Request',
		description: 'Title displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFormSectionTitle: {
		defaultValue: 'Fulfillment Request',
		description: 'Title displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Section Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFormPageDescription: {
		defaultValue: '',
		description: 'Description displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Page Description`,
		displayType: 'Content',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: true,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFormInstructions: {
		defaultValue: instructionHTML,
		description: 'Instructions displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: true,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadVoucherCodeLabel: {
		defaultValue: 'Enter Voucher Token',
		description: 'Label for the voucher code input.',
		displayPath: `${dpRoot}|Voucher Code Label`,
		displayType: 'Label',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadVoucherCodeRequired: {
		defaultValue: 'Voucher token is required',
		description: 'Error message for the voucher code input.',
		displayPath: `${dpRoot}|Voucher Code Required`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFcatIdLabel: {
		defaultValue: 'Fulfillment',
		description: 'Label for the fulfillment ID input.',
		displayPath: `${dpRoot}|Fcat ID Label`,
		displayType: 'Label',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFcatIdRequired: {
		defaultValue: 'Fulfillment is required',
		description: 'Error message for the fulfillment ID input.',
		displayPath: `${dpRoot}|Fulfillment ID Required`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadSubmitButton: {
		defaultValue: 'Claim Voucher',
		description: 'Submit button for the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Submit Button`,
		displayType: 'Button',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadSuccess: {
		defaultValue: 'Voucher claimed successfully',
		description: 'Success message for the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Success`,
		displayType: 'Notification',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadError: {
		defaultValue: 'There was a problem claiming the voucher.',
		description: 'Error message for the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Error`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadErrorNotFound: {
		defaultValue: 'The voucher code provided is not valid.',
		description: 'Error message for the voucher not found error.',
		displayPath: `${dpRoot}|Error Not Found`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	CPFulfillmentVoucherUploadFormNoFulfillments: {
		defaultValue: '<p>No fulfillments are available for your account.</p>',
		description: 'Message displayed when no fulfillments are available for the account.',
		displayPath: `${dpRoot}|No Fulfillments`,
		displayType: 'Content',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: true,
		shared: false,
		portal: 'CP',
	},
	'11dd20b85ec544efb80d50ae91040ae6': {
		defaultValue: 'Voucher code has already been redeemed.',
		description: 'Error message displayed when the voucher code has already been redeemed.',
		displayPath: `${dpRoot}|Voucher Code Redeemed`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',	
	},
};

export const cpFulfillmentVoucherUploadFormLocKeys = MapLocKeys<CPFulfillmentVoucherUploadFormLocs>(
	cpFulfillmentVoucherUploadFormLocs
);

export type CPFulfillmentVoucherUploadFormLocKeys = typeof cpFulfillmentVoucherUploadFormLocKeys;
export const isCPFulfillmentVoucherUploadFormLocKey = (key: string): key is keyof CPFulfillmentVoucherUploadFormLocKeys => {
	return Object.keys(cpFulfillmentVoucherUploadFormLocKeys).includes(key);
};
